import { get } from "lodash";
import { createContext, Fragment, ReactChild, ReactFragment, ReactPortal, useContext, useEffect, useState } from "react";
import { Button, Spin } from "antd";
import "./summaryReport.scss";
import Logo from "@assets/logo-compressed.png";
import QualCareLogo from "@assets/images/qualCareLogo.png";
import DemoLogo from "@assets/images/dashboardReport.png";
import envelop_png from "@assets/images/envelop_png.png";
import internet_png from "@assets/images/internet_png.png";
import Icon from "@assets/icon_config";
import memberDetailsService from "@services/memberDetails.service";
import membersService from "@services/members.service";
import { formatDisplayDate } from "@utils/helpers";
import { usePdfContext } from "../../overview/pdf";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../../constants/constants";
import RefreshData from "@components/refreshData/refreshData";
import CPSRNotes from "@pages/member-details/care-coordination/components/care-plan-report/patientNotes/patientNotes";
import moment from "moment";

const useSummaryReport = (props: any) => {
  const { setisOpen : setIsOpen } = usePdfContext();

  const [summaryReportData, setSummaryReportData] = useState<any>({});
  const [isModalLoading, setIsModalLoading] = useState(false);

  useEffect(() => {
    setIsOpen && setIsOpen(false);
  }, []);

  const getSummaryReportData = (patientId: string) => {
    setIsModalLoading(true);
    setSummaryReportData({});
    memberDetailsService.fetchSummaryReport(patientId).subscribe({
      next: (v) => {
        const response = v.data.data;
        setSummaryReportData(response);
        setIsModalLoading(false);
        setIsOpen && setIsOpen(true);
      },
      error: (e) => {
        setIsModalLoading(false);
        setIsOpen && setIsOpen(false);
      },
    });
  };

  const onPdfOpen = () => {
    getSummaryReportData(membersService?.getCurrentMember?.patient_id);
  };

  return {
    summaryReportData,
    isModalLoading,
    onPdfOpen
  };
};

const SummaryReportContext = createContext<any>({});

export const SummaryReportProvider = ({ children, ...props }: any) => {
  const summaryReport = useSummaryReport(props);
  return <SummaryReportContext.Provider value={summaryReport}>{children}</SummaryReportContext.Provider>;
};

export const useSummaryReportContext = () => useContext(SummaryReportContext);

export const PdfOpenButton = (props: { title: string; reviewReason?: any }) => {
  const { onPdfOpen, isModalLoading } = useSummaryReportContext();
  useEffect(() => { 
    if (props.reviewReason) {
      onPdfOpen && onPdfOpen();
    }
  }, [props.reviewReason]);

  return (
    <Spin spinning={isModalLoading} indicator={<HealthemSpinner />}>
      <Button
        type="default"
        color="black"
        className="pdf-action-button"
        onClick={(e) => {
          e.stopPropagation();
          onPdfOpen && onPdfOpen();
        }}
        size="large"
      style={{ height: "2.6rem"}}
      >
        <Icon name="pdf" />
        {props.title}
      </Button>
    </Spin>
  );
};

const SummaryReport = (props: any) => {
  const { summaryReportData, isModalLoading } = useSummaryReportContext();

  let headerLogo = QualCareLogo;

  if (process.env.REACT_APP_ENVIRONMENT === "demo") {
    headerLogo = DemoLogo;
  }
  
  const valueFormatter: any = {
    DOB: (value: string) => {
      const dobAge = value.split('#AGE#')
      return (dobAge[0] !== "--" ? dobAge[0] : dobAge[0]) + " " + dobAge[1];
    }
  };
  
  const patientNotes =  {
    title: "Patient Notes",
    notes: summaryReportData?.notes
  }

  return (
    <Spin spinning={isModalLoading} indicator={<HealthemSpinner size={70}/>}>
      <div className="ui-summary-review">
        {/* Header */}
        <div className="ui-summary-review__patient-info">
          <img src={headerLogo} alt="company logo" className="ui-summary-review__patient-info__logo" />
          <div className="ui-summary-review__patient-info__content">
            {summaryReportData?.patientInfo?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
              return (
                <Fragment key={item.label}>
                  <div className="ui-summary-review__patient-info__content__data__label">{item.label}</div>
                  <div className={`ui-summary-review__patient-info__content__data__value ${item.label.toLowerCase().replaceAll(" ", "-")}`}>{item.value}</div>
                </Fragment>
              );
            })}
          </div>
        </div>
        <div className="ui-summary-review__title mt-1">
          <div className="ui-summary-review__title__text">Summary Report </div>
          <span className="date"> <span className="date__prefix">As of&nbsp; </span>{moment().format("YYYY-MM-DD")}</span>
        </div>

         {/* Patient Details */}
        <hr className="ui-summary-review__division ui-summary-review__division--top"></hr>
        <div className="ui-summary-review__title">
          <div className="ui-summary-review__title__subtitle">Patient Details</div>
        </div>
        <div className="ui-summary-review__report-review">
          <div className="ui-summary-review__report-review__left">
            {summaryReportData?.reportReview?.slice(0, 3)?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
              let value = valueFormatter[item.label] ? valueFormatter[item.label](item.value) : item.value;
              return (
                <div key={item.label} className="ui-summary-review__report-review__left__data">
                  <div className="ui-summary-review__report-review__left__data__label">{item.label}</div>
                  <div className="ui-summary-review__report-review__left__data__value">{value}</div>
                </div>
              );
            })}
          </div>
          <div className="ui-summary-review__report-review__right">
            {summaryReportData?.reportReview
              ?.slice(3, 6)
              ?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
                let value = valueFormatter[item.label] ? valueFormatter[item.label](item.value) : item.value;
                return (
                  <div key={item.label} className="ui-summary-review__report-review__right__data">
                    <div className="ui-summary-review__report-review__right__data__label">{item.label}</div>
                    <div className="ui-summary-review__report-review__right__data__value">{value}</div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* summary part */}
        <hr className="ui-summary-review__division ui-summary-review__division"></hr>
        <div className="ui-summary-review__title">
        <div className="ui-summary-review__title__subtitle">Summary</div>
        </div>
        <div className="ui-summary-review__report-review-summary-part">
          <div className="ui-summary-review__report-review-summary-part__table">
            <div className="ui-summary-review__report-review-summary-part__table__left">
              {summaryReportData?.summaryReview?.slice(0, 4)?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
                let value = valueFormatter[item.label] ? valueFormatter[item.label](item.value) : item.value;
                return (
                  <div key={item.label} className="ui-summary-review__report-review-summary-part__table__left__data">
                    <div className="ui-summary-review__report-review-summary-part__table__left__data__label">{item.label}</div>
                    <div className="ui-summary-review__report-review-summary-part__table__left__data__value">{value}</div>
                  </div>
                );
              })}
            </div>
            <div className="ui-summary-review__report-review-summary-part__table__right">
              {summaryReportData?.summaryReview?.slice(4, 8)?.map((item: { label: string; value: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined }) => {
                return (
                  <div key={item.label} className="ui-summary-review__report-review-summary-part__table__right__data">
                    <div className="ui-summary-review__report-review-summary-part__table__right__data__label">{item.label}</div>
                    <div className="ui-summary-review__report-review-summary-part__table__right__data__value">{item.value}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Unique Diagnosis Codes */}
        <hr className="ui-summary-review__division"></hr>
        <div className="ui-summary-review__title">
          <div className="ui-summary-review__title__subtitle">Unique Diagnosis Codes </div>
        </div>
        <div className="ui-summary-review__report-review-diagnosis-code-part">
          <TableView
            dataSource={summaryReportData?.uniqueDiagnosisCode}
            columnKeyCombo={[
              {
                column: "HCC CATEGORY",
                key: "hccCategory",
              },
              {
                column: "HCC CAPTURED IN CURRENT YEAR",
                key: "hccCapturedInCurrentYear",
              },
              {
                column: "DIAGNOSIS CODES AND DESCRIPTION",
                key: "diagnosisCode",
              },
              {
                column: "ICD CAPTURED IN CURRENT YEAR",
                key: "icdCapturedInCurrentYear",
              },
              {
                column: "IS V28?",
                key: "isV28",
              },
            ]}
            showDividers={true}
            tableType="uniqueDiagnosisCode"
          />
        </div>

        {/* Care Gaps part */}
        <hr className="ui-summary-review__division"></hr>
        <div className="ui-summary-review__title">
          <div className="ui-summary-review__title__subtitle">Measures with Care Gaps (PY {new Date().getFullYear()})</div>
        </div>
        <div className="ui-summary-review__report-review-care-gaps-part">
          <div className="ui-summary-review__report-review-care-gaps-part__table">
            <TableView
              dataSource={summaryReportData?.careGapInfo}
              columnKeyCombo={[
                {
                  column: "Measure Name",
                  key: "measureName",
                },
                {
                  column: "Has Gap?",
                  key: "hasGap",
                },
                {
                  column: "Comment",
                  key: "comment",
                },
              ]}
              customRenderer={careGapsRenderer}
              tableType="careGaps"
            />
          </div>
        </div>

        {/* Notes part */}
        <hr className="ui-summary-review__division"></hr>
        <CPSRNotes data={patientNotes} />
        
        {/* Footer */}
        <div className="ui-summary-review__footer mt-1">
          <img src={Logo} alt="company logo" className="ui-summary-review__footer__logo" />
          <div className="ui-summary-review__footer__content">
            <div className="ui-summary-review__footer__content__data">
              <div className="ui-summary-review__footer__content__data__icon">
                <img src={envelop_png} alt="company logo" />
              </div>
              <div className="ui-summary-review__footer__content__data__value">support.healthem@tredence.com</div>
            </div>
            <div className="ui-summary-review__footer__content__data">
              <div className="ui-summary-review__footer__content__data__icon">
                <img src={internet_png} alt="company logo" />
              </div>
              <div className="ui-summary-review__footer__content__data__value">https://www.healthem.ai</div>
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

const careGapsRenderer = (value: any, key: string) => {
  if (key === "hasGap") {
    return <span style={{ color: value === "1" || value === 1 ? "#1BBE75" : "#E75547" }}>{value === "1" || value === 1 ? "Yes" : "No"}</span>;
  }

  return value;
};

const TableView = (props: { dataSource: any; columnKeyCombo: any[]; showDividers?: boolean; tableType?: "default" | "careGaps" | "uniqueDiagnosisCode"; customRenderer?: (value: any, key: string) => any }) => {
  const { dataSource, columnKeyCombo, showDividers, tableType, customRenderer } = props;
  const memberStatus = membersService?.getCurrentMember?.patient_status
  const isMemberActive = memberStatus === "Active"

  return (
    <table className="ui-summary-review__table-view">
      <thead>
        {showDividers && (
          <tr>
            <th colSpan={columnKeyCombo.length}>
              <hr className="ui-summary-review__table-view__column-division" />
            </th>
          </tr>
        )}
          <tr className="ui-summary-review__table-view__head mb-1">
            {columnKeyCombo?.map((item, index) => (
              <th key={item.column + index}>{item.column}</th>
            ))}
          </tr>
        {tableType === "careGaps" && <br />}
      </thead>
     
      {showDividers && (
        <tr>
          <th colSpan={columnKeyCombo.length}>
            <hr className="ui-summary-review__table-view__column-division" />
          </th>
        </tr>
      )}

      <tbody className="ui-summary-review__table-view__body">
        {dataSource && dataSource.length > 0 ? (
          dataSource.map((item: any, rowIndex: number) => {
            return (
              <Fragment key={rowIndex}>
                <tr className="ui-summary-review__table-view__body__row">
                  {columnKeyCombo?.map((_item, colIndex) => {
                    let cellValue = get(item, _item.key, "");

                    if (_item.key === "diagnosisCode") {
                      const description = get(item, "description", "");
                      cellValue = `${cellValue} - ${description}`;
                    }

                    return (
                      <td
                        key={rowIndex + "-" + colIndex}
                        className={`ui-summary-review__table-view__body__row__${colIndex === 0 ? "label" : "value"} ${tableType === "uniqueDiagnosisCode" ? "unique-diagnosis" : ""}`}
                      >
                        {customRenderer ? customRenderer(cellValue, _item.key) : cellValue}
                      </td>
                    );
                  })}
                </tr>

                {tableType === "uniqueDiagnosisCode" && rowIndex !== dataSource.length - 1 && (
                  <tr key={`divider-${rowIndex}`} className="ui-summary-review__table-view__border-row">
                    <td colSpan={columnKeyCombo.length}>
                      <hr className="ui-summary-review__table-view__array-division" />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })
        ) : (
          <tr>
            <td colSpan={columnKeyCombo.length} className="text-center">
              <DataNotFound message={NO_DATA_FOUND} />
            </td>
          </tr>
        )}
      </tbody>

      {tableType === "careGaps" && (
        <tfoot>
          <tr>
            <td colSpan={columnKeyCombo.length} className="text-center">
              <p className="ui-summary-review__footer-note">
                {isMemberActive ? <RefreshData /> : "Note: Care gaps are calculated only for active patients."}
              </p>
            </td>
          </tr>
        </tfoot>
      )}

    </table>
  );
};

export default SummaryReport;